"use client";
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type SidebarDensity = "compact" | "comfortable";
export type AccentColor = "gold" | "blue" | "violet" | "cyan" | "green";

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  resolvedTheme: "light" | "dark";
  sidebarDensity: SidebarDensity;
  setSidebarDensity: (density: SidebarDensity) => void;
  accentColor: AccentColor;
  setAccentColor: (color: AccentColor) => void;
  fontSize: "small" | "medium" | "large";
  setFontSize: (size: "small" | "medium" | "large") => void;
  sidebarWidth: number;
  setSidebarWidth: (w: number) => void;
  compactNav: boolean;
  setCompactNav: (v: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const ACCENT_COLORS: Record<AccentColor, { primary: string; primaryFg: string; ring: string; gold: string }> = {
  gold:   { primary: "#b8943f", primaryFg: "#ffffff", ring: "#b8943f", gold: "rgba(184,148,63,0.08)" },
  blue:   { primary: "#2563eb", primaryFg: "#ffffff", ring: "#2563eb", gold: "rgba(37,99,235,0.08)" },
  violet: { primary: "#7c3aed", primaryFg: "#ffffff", ring: "#7c3aed", gold: "rgba(124,58,237,0.08)" },
  cyan:   { primary: "#0891b2", primaryFg: "#ffffff", ring: "#0891b2", gold: "rgba(8,145,178,0.08)" },
  green:  { primary: "#059669", primaryFg: "#ffffff", ring: "#059669", gold: "rgba(5,150,105,0.08)" },
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>("light");
  const [sidebarDensity, setSidebarDensityState] = useState<SidebarDensity>("comfortable");
  const [accentColor, setAccentColorState] = useState<AccentColor>("gold");
  const [fontSize, setFontSizeState] = useState<"small" | "medium" | "large">("medium");
  const [sidebarWidth, setSidebarWidthState] = useState(178);
  const [compactNav, setCompactNav] = useState(false);
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("ts_theme");
    if (saved) {
      try {
        const t = JSON.parse(saved);
        if (t.themeMode) setThemeModeState(t.themeMode);
        if (t.sidebarDensity) setSidebarDensityState(t.sidebarDensity);
        if (t.accentColor) setAccentColorState(t.accentColor);
        if (t.fontSize) setFontSizeState(t.fontSize);
        if (t.sidebarWidth) setSidebarWidthState(t.sidebarWidth);
        if (t.compactNav !== undefined) setCompactNav(t.compactNav);
      } catch {}
    }
    setMounted(true);
  }, []);

  // Resolve system theme
  useEffect(() => {
    if (!mounted) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const resolve = () => {
      if (themeMode === "system") {
        setResolvedTheme(mq.matches ? "dark" : "light");
      } else {
        setResolvedTheme(themeMode);
      }
    };
    resolve();
    mq.addEventListener("change", resolve);
    return () => mq.removeEventListener("change", resolve);
  }, [themeMode, mounted]);

  // Apply theme class to HTML
  useEffect(() => {
    if (!mounted) return;
    const html = document.documentElement;
    if (resolvedTheme === "dark") {
      html.classList.add("dark");
    } else {
      html.classList.remove("dark");
    }
  }, [resolvedTheme, mounted]);

  // Apply accent color CSS variables
  useEffect(() => {
    if (!mounted) return;
    const c = ACCENT_COLORS[accentColor];
    const root = document.documentElement;
    root.style.setProperty("--primary", c.primary);
    root.style.setProperty("--primary-foreground", c.primaryFg);
    root.style.setProperty("--ring", c.ring);
    root.style.setProperty("--accent", c.gold);
    root.style.setProperty("--sidebar-primary", c.primary);
    root.style.setProperty("--sidebar-accent", c.gold);
    root.style.setProperty("--sidebar-ring", c.ring);
  }, [accentColor, mounted]);

  // Apply font size
  useEffect(() => {
    if (!mounted) return;
    const sizes = { small: "13px", medium: "14px", large: "15px" };
    document.documentElement.style.fontSize = sizes[fontSize];
  }, [fontSize, mounted]);

  // Persist to localStorage
  const persist = useCallback((overrides: Record<string, any>) => {
    const current = {
      themeMode, sidebarDensity, accentColor, fontSize, sidebarWidth, compactNav,
      ...overrides,
    };
    localStorage.setItem("ts_theme", JSON.stringify(current));
  }, [themeMode, sidebarDensity, accentColor, fontSize, sidebarWidth, compactNav]);

  const setThemeMode = (mode: ThemeMode) => { setThemeModeState(mode); persist({ themeMode: mode }); };
  const setSidebarDensity = (d: SidebarDensity) => {
    setSidebarDensityState(d);
    // Compact = 148px, comfortable = 178px
    const w = d === "compact" ? 148 : 178;
    setSidebarWidthState(w);
    persist({ sidebarDensity: d, sidebarWidth: w });
  };
  const setAccentColor = (c: AccentColor) => { setAccentColorState(c); persist({ accentColor: c }); };
  const setFontSize = (s: "small" | "medium" | "large") => { setFontSizeState(s); persist({ fontSize: s }); };
  const setSidebarWidth = (w: number) => { setSidebarWidthState(w); persist({ sidebarWidth: w }); };
  const setCompactNavFn = (v: boolean) => { setCompactNav(v); persist({ compactNav: v }); };

  return (
    <ThemeContext.Provider value={{
      themeMode, setThemeMode, resolvedTheme,
      sidebarDensity, setSidebarDensity,
      accentColor, setAccentColor,
      fontSize, setFontSize,
      sidebarWidth, setSidebarWidth,
      compactNav, setCompactNav: setCompactNavFn,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
